import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { GetConvDivListService } from './api-services/get-conv-div-list.service';
import { ConvDivComboBoxModel } from './conv-div-combo-box.model';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 カテゴリ変換区分コンボボックスを制御するクラス
 *
 * @export ConvDivComboBoxComponent
 * @class ConvDivComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 */
@Component({
  selector: 'app-conv-div-combo-box',
  templateUrl: 'conv-div-combo-box.component.html',
  styleUrls: ['conv-div-combo-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ConvDivComboBoxComponent,
      multi: true
    },
    {
      provide: GetConvDivListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetConvDivListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class ConvDivComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() public tabindex: number = null;
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: string;                                                  // ロード完了後フォーカスを移動させるid
  @Input() public categoryDiv: string = <string>'0';
  @Input() selectedMiddleClassCodeCtrl: boolean = <boolean>true;
  @Input() selectedSmallClassCodeCtrl: boolean = <boolean>true;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() autofocus: boolean = <boolean>false;
  @Input() forcedEnabledMode: string = null;

  /** コンボボックスの参照 */
  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  public selectedConvDiv: string = null;
  public selectedClassCodeCtrl: boolean = <boolean>true;
  public selectedLargeClassCodeCtrl: boolean = <boolean>true;
  public selectedMarkCode1Ctrl: boolean = <boolean>true;
  public selectedMarkCode2Ctrl: boolean = <boolean>true;
  public selectedMarkCode3Ctrl: boolean = <boolean>true;
  public selectedPricePatternCtrl: boolean = <boolean>true;
  public selectedMediumTypeCtrl: boolean = <boolean>true;
  public selectedFeatureCodeCtrl: boolean = <boolean>true;
  public selectedPromotionDivCtrl: boolean = <boolean>true;
  public selectedPageNumCtrl: boolean = <boolean>true;
  public selectedExhibitionDivCtrl: boolean = <boolean>true;
  public selectedTag1Ctrl: boolean = <boolean>true;
  public selectedTag2Ctrl: boolean = <boolean>true;
  public selectedDnaDiv1Ctrl: boolean = <boolean>true;
  public selectedDnaDiv2Ctrl: boolean = <boolean>true;
  public selectedDnaDiv3Ctrl: boolean = <boolean>true;
  public selectedIndex: number = <number>-1;                                  // 選択した番号
  public model: ConvDivComboBoxModel = new ConvDivComboBoxModel();

  selectedItem: any;

  constructor(
    public elementRef: ElementRef,
    public getConvDivListService: GetConvDivListService
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedConvDiv) {
      this.selectedConvDiv = text;
      this.setConvDiv(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期処理
   *
   * @memberof ConvDivComboBoxComponent
   */
  ngOnInit(): void {
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof ConvDivComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getConvDivListService.execute(this.model, this.categoryDiv);
    }
  }

  /**
   * コンポーネント終了時
   *
   * @memberof ConvDivComboBoxComponent
   */
  ngOnDestroy(): void {
    this.selectedConvDiv = null;
    this.model = null;
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof ConvDivComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンボボックスの選択値を切り替える
   *
   * @param {string} convDiv 変換区分
   * @memberof ConvDivComboBoxComponent
   */
  public setConvDiv(convDiv: string): void {
    this.model.setConvDiv = convDiv;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedConvDiv();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof ConvDivComboBoxComponent
   */
  public updateSelectedConvDiv(): void {
    const convDivList: Array<any> = this.model.convDivList;
    for (let i = Number(0); i < convDivList.length; i++) {
      if (convDivList[i].ConvDiv === this.model.setConvDiv) {
        this.selectedIndex = i;
        this.updateBindedField(null);
      }
    }
    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof ConvDivComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedItem = null;
      this.selectedConvDiv = null;
      this.selectedClassCodeCtrl = <boolean>true;
      this.selectedLargeClassCodeCtrl = <boolean>true;
      this.selectedMiddleClassCodeCtrl = <boolean>true;
      this.selectedSmallClassCodeCtrl = <boolean>true;
      this.selectedMarkCode1Ctrl = <boolean>true;
      this.selectedMarkCode2Ctrl = <boolean>true;
      this.selectedMarkCode3Ctrl = <boolean>true;
      this.selectedPricePatternCtrl = <boolean>true;
      this.selectedMediumTypeCtrl = <boolean>true;
      this.selectedFeatureCodeCtrl = <boolean>true;
      this.selectedPromotionDivCtrl = <boolean>true;
      this.selectedPageNumCtrl = <boolean>true;
      this.selectedExhibitionDivCtrl = <boolean>true;
      this.selectedTag1Ctrl = <boolean>true;
      this.selectedTag2Ctrl = <boolean>true;
      this.selectedDnaDiv1Ctrl = <boolean>true;
      this.selectedDnaDiv2Ctrl = <boolean>true;
      this.selectedDnaDiv3Ctrl = <boolean>true;

      return;
    }

    this.selectedItem = this.model.convDivList[this.selectedIndex];
    this.selectedConvDiv = this.selectedItem.ConvDiv;
    this.selectedClassCodeCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.ClassCodeCtrl);
    this.selectedLargeClassCodeCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.LargeClassCodeCtrl);
    this.selectedMiddleClassCodeCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.MiddleClassCodeCtrl);
    this.selectedSmallClassCodeCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.SmallClassCodeCtrl);
    this.selectedMarkCode1Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.MarkCode1Ctrl);
    this.selectedMarkCode2Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.MarkCode2Ctrl);
    this.selectedMarkCode3Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.MarkCode3Ctrl);
    this.selectedPricePatternCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.PricePatternCtrl);
    this.selectedMediumTypeCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.MediumTypeCtrl);
    this.selectedFeatureCodeCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.FeatureCodeCtrl);
    this.selectedPromotionDivCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.PromotionDivCtrl);
    this.selectedPageNumCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.PageNumCtrl);
    this.selectedExhibitionDivCtrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.ExhibitionDivCtrl);
    this.selectedTag1Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.Tag1Ctrl);
    this.selectedTag2Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.Tag2Ctrl);
    this.selectedDnaDiv1Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.DnaDiv1Ctrl);
    this.selectedDnaDiv2Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.DnaDiv2Ctrl);
    this.selectedDnaDiv3Ctrl = PlessViewUtilsSub.StringTOBoolean(this.selectedItem.DnaDiv3Ctrl);

  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof ConvDivComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.setSelectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof ConvDivComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.setSelectedIndex >= 0) {
      this.selectedIndex = <number>this.model.setSelectedIndex;
      this.model.setSelectedIndex = -1;
      this.updateBindedField(null);
    }
  }

  /**
   * setFocusOverride※テキスト選択解除の為
   *
   * @memberof ConvDivComboBoxComponent
   */
  public setFocus(): void {
    this.comboBox.nativeElement.focus();
  }

}
